import { useMemo, useState } from 'react';
import { Card } from '../../components/Card/Card';
import { MoneyInput } from '../../components/MoneyInput/MoneyInput';
import { SegmentedToggle } from '../../components/SegmentedToggle/SegmentedToggle';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useUiStore } from '../../store/ui';
import {
  useCreateCategory,
  useCreateItem,
  useDeleteCategory,
  useDeleteItem,
  useUpdateCategory,
  useUpdateItem,
  useYearBundle,
} from '../../store/queryHooks';
import { fmtN, signN } from '../../lib/format';
import { MONTH_SHORT } from '../../lib/dates';
import type { Category } from '@budgetx/shared';
import styles from './YearTablePage.module.css';

const NEW_CATEGORY_LABEL: Record<'income' | 'fast' | 'variabel', string> = {
  income: 'Ny indtægtskategori',
  fast: 'Ny udgiftskategori',
  variabel: 'Ny kategori',
};

const VARIABLE_NAMES = new Set(['Dagligvarer', 'Børn & fritid']);

function categoryGroup(c: Category): 'income' | 'fast' | 'variabel' {
  if (c.kind === 'income') return 'income';
  if (c.kind === 'variabel' || VARIABLE_NAMES.has(c.name)) return 'variabel';
  return 'fast';
}

type ConfirmTarget =
  | { kind: 'item'; id: number; name: string }
  | { kind: 'category'; id: number; name: string; itemCount: number };

interface Props {
  kind: 'income' | 'fast' | 'variabel';
}

export function YearTablePage({ kind }: Props) {
  const {
    year,
    month,
    openCats,
    toggleCat,
    openCat,
    yearMode,
    setYearMode,
    editNames,
    toggleEditNames,
    setToast,
  } = useUiStore();
  const bundle = useYearBundle(year);
  const updateItem = useUpdateItem(year);
  const updateCategory = useUpdateCategory(year);
  const createCategory = useCreateCategory(year);
  const createItem = useCreateItem(year);
  const deleteItem = useDeleteItem(year);
  const deleteCategory = useDeleteCategory(year);
  const [confirmDelete, setConfirmDelete] = useState<ConfirmTarget | null>(null);
  const namesEditable = yearMode === 'budget' && editNames;

  const cats = useMemo(
    () => (bundle.data?.categories ?? []).filter((c) => categoryGroup(c) === kind),
    [bundle.data, kind],
  );

  if (bundle.isPending) return <div>Indlæser…</div>;
  if (bundle.isError || !bundle.data) return <div>Kunne ikke hente året.</div>;

  const isIncome = kind === 'income';

  const totalsPerMonth = (getVal: (it: Category['items'][number], m: number) => number | null) => {
    const arr: (number | null)[] = Array(12).fill(null);
    for (let m = 0; m < 12; m++) {
      let sum = 0;
      let any = false;
      for (const c of cats) {
        for (const it of c.items) {
          const v = getVal(it, m);
          if (v !== null) {
            sum += v;
            any = true;
          }
        }
      }
      arr[m] = any ? sum : null;
    }
    return arr;
  };

  const budgetsPerMonth = totalsPerMonth((it, m) => it.budgets[m] ?? 0);
  // Faktisk mode: past/current months with no actual are counted as 0 so
  // the year-level diff row surfaces the missing entries. Future months
  // still stay null (nothing recorded yet).
  const actualsPerMonth = totalsPerMonth((it, m) => {
    const v = it.actuals[m];
    if (v !== null && v !== undefined) return v;
    if (yearMode === 'faktisk' && m <= month) return 0;
    return null;
  });
  // True for month m when no item across any of these categories has any
  // recorded actual — Faktisk view then falls back to showing the budget.
  const noEntryPerMonth = Array(12)
    .fill(0)
    .map((_, m) =>
      cats.every((c) =>
        c.items.every((it) => it.actuals[m] === null || it.actuals[m] === undefined),
      ),
    );

  const label = {
    income: 'Indtægter i alt',
    fast: 'Faste udgifter i alt',
    variabel: 'Variable udgifter i alt',
  }[kind];

  async function patchCell(
    it: Category['items'][number],
    m: number,
    field: 'budget' | 'actual',
    next: number | null,
  ) {
    try {
      if (field === 'budget') {
        const budgets = it.budgets.slice();
        budgets[m] = next ?? 0;
        await updateItem.mutateAsync({ id: it.id, budgets: budgets as number[] });
      } else {
        const actuals = it.actuals.slice();
        actuals[m] = next;
        await updateItem.mutateAsync({ id: it.id, actuals: actuals as (number | null)[] });
      }
    } catch {
      setToast('Kunne ikke gemme');
    }
  }

  async function patchCategoryName(id: number, current: string, next: string) {
    if (current === next) return;
    try {
      await updateCategory.mutateAsync({ id, name: next });
    } catch {
      setToast('Kunne ikke gemme kategorinavn');
    }
  }

  async function patchItemName(it: Category['items'][number], next: string) {
    if (it.name === next) return;
    try {
      await updateItem.mutateAsync({ id: it.id, name: next });
    } catch {
      setToast('Kunne ikke gemme navn');
    }
  }

  async function addToCategory(c: Category) {
    try {
      await createItem.mutateAsync({ categoryId: c.id, name: 'Ny postering' });
      setToast(`Ny postering tilføjet til alle måneder i ${year}`);
    } catch {
      setToast('Kunne ikke tilføje postering');
    }
  }

  async function addCategoryForKind() {
    const name = NEW_CATEGORY_LABEL[kind];
    try {
      const created = await createCategory.mutateAsync({ name, kind });
      if (!created) throw new Error('missing category response');
      await createItem.mutateAsync({ categoryId: created.id, name: 'Ny postering' });
      openCat(created.name);
      setToast('Ny kategori tilføjet');
    } catch {
      setToast('Kunne ikke tilføje kategori');
    }
  }

  function requestDeleteItem(id: number, name: string) {
    setConfirmDelete({ kind: 'item', id, name });
  }

  function requestDeleteCategory(c: Category) {
    setConfirmDelete({ kind: 'category', id: c.id, name: c.name, itemCount: c.items.length });
  }

  async function performDelete() {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.kind === 'item') {
        await deleteItem.mutateAsync(confirmDelete.id);
      } else {
        await deleteCategory.mutateAsync(confirmDelete.id);
      }
    } catch {
      setToast('Kunne ikke slette');
    } finally {
      setConfirmDelete(null);
    }
  }

  const yearBudgetTotal = budgetsPerMonth.reduce<number>((s, v) => s + (v ?? 0), 0);
  const yearActualTotal = actualsPerMonth.reduce<number>((s, v) => s + (v ?? 0), 0);
  const yearDiff = isIncome ? yearActualTotal - yearBudgetTotal : yearBudgetTotal - yearActualTotal;

  return (
    <div className={styles.wrap}>
      <Card className={styles.card}>
        <div className={styles.header}>
          <div className={styles.headerControls}>
            <SegmentedToggle
              options={[
                { value: 'faktisk', label: 'Faktisk' },
                { value: 'budget', label: 'Budget' },
              ]}
              value={yearMode}
              onChange={setYearMode}
            />
            {yearMode === 'budget' && (
              <button
                type="button"
                className={`${styles.editToggle} ${editNames ? styles.editToggleOn : ''}`}
                onClick={toggleEditNames}
                title="Slå navneredigering til eller fra"
                aria-pressed={editNames}
              >
                <span className={styles.editKnobTrack}>
                  <span className={styles.editKnobDot} />
                </span>
                Rediger navne: {editNames ? 'til' : 'fra'}
              </button>
            )}
          </div>
          <span className={styles.hint}>
            Faktiske tal indtastes pr. måned på Budget · tomme måneder mangler endnu
          </span>
        </div>

        <div className={styles.scroll}>
          <div className={styles.inner}>
            <div className={styles.headRow}>
              <div className={styles.headCell}>Kategori</div>
              {MONTH_SHORT.map((m, i) => (
                <div
                  key={m}
                  className={`${styles.monthCell} ${i === month ? styles.monthCurrent : ''}`}
                >
                  {m}
                </div>
              ))}
              <div className={styles.totalHead}>I alt</div>
              <div className={styles.headSpacer} />
            </div>

            {cats.map((c) => {
              const isOpen = openCats.includes(c.name);
              const catB = Array(12)
                .fill(0)
                .map((_, m) => c.items.reduce((s, it) => s + (it.budgets[m] ?? 0), 0));
              // In Faktisk mode, months up to the current month with no
              // recorded actual are treated as 0 (the budget was missed /
              // nothing spent) so the diff column reflects that shortfall.
              // Future months stay null = not yet.
              const catF = Array(12)
                .fill(0)
                .map((_, m) => {
                  const treatEmptyAsZero = yearMode === 'faktisk' && m <= month;
                  let sum = 0;
                  let any = false;
                  for (const it of c.items) {
                    const v = it.actuals[m];
                    if (v !== null && v !== undefined) {
                      sum += v;
                      any = true;
                    } else if (treatEmptyAsZero) {
                      any = true;
                    }
                  }
                  return any ? sum : null;
                });
              // True for month m when no item in this category has any recorded
              // actual there — in Faktisk view we then show the budget subtly
              // and the -budget diff in red, so an unfilled cell doesn't hide
              // its planned amount.
              const catNoEntry = Array(12)
                .fill(0)
                .map((_, m) => c.items.every((it) => it.actuals[m] === null || it.actuals[m] === undefined));
              const catTF = catF.reduce<number>((s, v) => s + (v ?? 0), 0);
              const catTB = catB.reduce((s, v) => s + v, 0);
              const catDiff = isIncome ? catTF - catTB : catTB - catTF;
              const catDiffOk = catDiff >= 0;

              return (
                <div key={c.id}>
                  <div className={styles.group}>
                    <div className={styles.groupRow}>
                      <div className={styles.groupName}>
                        <span className={styles.chevron}>{isOpen ? '–' : '+'}</span>
                        {namesEditable ? (
                          <input
                            className={styles.groupNameInput}
                            value={c.name}
                            onChange={(e) => patchCategoryName(c.id, c.name, e.target.value)}
                            onFocus={(e) => e.target.select()}
                          />
                        ) : (
                          <button
                            type="button"
                            className={styles.groupNameStatic}
                            onClick={() => toggleCat(c.name)}
                          >
                            {c.name}
                          </button>
                        )}
                      </div>
                      <button className={styles.groupToggleBtn} onClick={() => toggleCat(c.name)}>
                        {MONTH_SHORT.map((_, m) => {
                          const b = catB[m] ?? 0;
                          const f = catF[m];
                          const inFaktisk = yearMode === 'faktisk';
                          const emptyFaktisk = inFaktisk && catNoEntry[m];
                          // Behold "—" som hovedværdi ved tom Faktisk, så man
                          // ikke forveksler budgettet med et registreret beløb.
                          // Den lille -budget-diff nedenunder signalerer det
                          // manglende beløb.
                          const val = yearMode === 'budget' ? b : emptyFaktisk ? null : f;
                          const showDiff = inFaktisk && (emptyFaktisk ? b > 0 : f !== null);
                          const d = emptyFaktisk ? -b : isIncome ? (f ?? 0) - b : b - (f ?? 0);
                          const bad =
                            inFaktisk && !emptyFaktisk && f != null && (isIncome ? f < b : f > b);
                          return (
                            <div key={m} className={styles.cellNum}>
                              <div
                                className={`${styles.cellFmt} ${bad ? styles.cellFmtOver : ''} ${yearMode === 'budget' ? styles.cellFmtBudget : ''}`}
                              >
                                {val === null || val === undefined ? '—' : fmtN(val)}
                              </div>
                              {showDiff && (
                                <div
                                  className={`${styles.cellSub} ${emptyFaktisk ? styles.cellSubOver : d >= 0 ? styles.cellSubOk : styles.cellSubOver}`}
                                >
                                  {signN(d)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div className={styles.totalCell}>
                          <div className={styles.totalPrimary}>{fmtN(yearMode === 'budget' ? catTB : catTF)}</div>
                          {yearMode === 'faktisk' && (
                            <div
                              className={`${styles.totalDiff} ${catDiffOk ? styles.cellSubOk : styles.cellSubOver}`}
                            >
                              {signN(catDiff)}
                            </div>
                          )}
                        </div>
                      </button>
                    </div>
                    <button
                      className={styles.deleteBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        requestDeleteCategory(c);
                      }}
                      title={`Slet kategorien ${c.name}`}
                      aria-label={`Slet kategorien ${c.name}`}
                    >
                      ×
                    </button>
                  </div>

                  {isOpen &&
                    c.items.map((it) => {
                      const tf = it.actuals.reduce<number>((s, v) => s + (v ?? 0), 0);
                      const tb = it.budgets.reduce((s, v) => s + v, 0);
                      const itDiff = isIncome ? tf - tb : tb - tf;
                      return (
                        <div key={it.id} className={styles.itemRow}>
                          {namesEditable ? (
                            <input
                              className={styles.itemNameInput}
                              value={it.name}
                              onChange={(e) => patchItemName(it, e.target.value)}
                              onFocus={(e) => e.target.select()}
                            />
                          ) : (
                            <div className={styles.itemNameStatic}>{it.name}</div>
                          )}
                          {MONTH_SHORT.map((_, m) => {
                            const b = it.budgets[m] ?? 0;
                            const aRaw = it.actuals[m] ?? null;
                            const inFaktisk = yearMode === 'faktisk';
                            const emptyFaktisk = inFaktisk && aRaw === null;
                            // Faktisk + tom: behold "—" i inputtet, men vis
                            // -budget som rød diff nedenunder, så man ser at
                            // en planlagt post endnu ikke er registreret.
                            const d = emptyFaktisk
                              ? -b
                              : isIncome
                                ? (aRaw ?? 0) - b
                                : b - (aRaw ?? 0);
                            const showDiff = inFaktisk && (emptyFaktisk ? b > 0 : aRaw !== null);
                            return (
                              <div key={m} className={styles.itemCell}>
                                <MoneyInput
                                  value={yearMode === 'budget' ? b : aRaw}
                                  onCommit={(v) =>
                                    patchCell(it, m, yearMode === 'budget' ? 'budget' : 'actual', v)
                                  }
                                  placeholder="—"
                                />
                                {showDiff && (
                                  <div
                                    className={`${styles.itemCellDiff} ${emptyFaktisk ? styles.cellSubOver : d >= 0 ? styles.cellSubOk : styles.cellSubOver}`}
                                  >
                                    {signN(d)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <div className={styles.totalCell}>
                            <div className={styles.totalPrimary}>{fmtN(yearMode === 'budget' ? tb : tf)}</div>
                            {yearMode === 'faktisk' && (
                              <div
                                className={`${styles.totalDiff} ${itDiff >= 0 ? styles.cellSubOk : styles.cellSubOver}`}
                              >
                                {signN(itDiff)}
                              </div>
                            )}
                          </div>
                          <button
                            className={styles.deleteBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDeleteItem(it.id, it.name);
                            }}
                            title={`Slet posteringen ${it.name}`}
                            aria-label={`Slet posteringen ${it.name}`}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  {isOpen && (
                    <div className={styles.addRow}>
                      <button className={styles.addBtn} onClick={() => addToCategory(c)}>
                        + Tilføj postering
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            <div className={styles.addCategoryRow}>
              <button className={styles.addCategoryBtn} onClick={addCategoryForKind}>
                + Tilføj kategori
              </button>
            </div>

            <div className={styles.grandTotal}>
              <div className={styles.grandLabel}>{label}</div>
              {MONTH_SHORT.map((_, m) => {
                const b = budgetsPerMonth[m] ?? 0;
                const f = actualsPerMonth[m];
                const inFaktisk = yearMode === 'faktisk';
                const emptyFaktisk = inFaktisk && noEntryPerMonth[m];
                const val = yearMode === 'budget' ? b : emptyFaktisk ? null : f;
                const d = emptyFaktisk ? -b : isIncome ? (f ?? 0) - b : b - (f ?? 0);
                const showDiff = inFaktisk && (emptyFaktisk ? b > 0 : f !== null);
                const bad =
                  inFaktisk && !emptyFaktisk && f != null && (isIncome ? f < b : f > b);
                return (
                  <div key={m} className={styles.cellNum}>
                    <div className={`${styles.cellFmt} ${bad ? styles.cellFmtOver : ''}`}>
                      {val === null || val === undefined ? '—' : fmtN(val)}
                    </div>
                    {showDiff && (
                      <div
                        className={`${styles.cellSub} ${emptyFaktisk ? styles.cellSubOver : d >= 0 ? styles.cellSubOk : styles.cellSubOver}`}
                      >
                        {signN(d)}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className={styles.totalCell}>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 600 }}>
                  {fmtN(yearMode === 'budget' ? yearBudgetTotal : yearActualTotal)}
                </div>
                {yearMode === 'faktisk' && (
                  <div
                    className={`${styles.totalDiff} ${yearDiff >= 0 ? styles.cellSubOk : styles.cellSubOver}`}
                  >
                    {signN(yearDiff)} vs. budget
                  </div>
                )}
              </div>
              <div className={styles.headSpacer} />
            </div>
          </div>
        </div>
      </Card>

      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.kind === 'item' ? 'Slet postering' : 'Slet kategori'}
          message={
            confirmDelete.kind === 'item'
              ? `Slet posteringen "${confirmDelete.name}"?`
              : confirmDelete.itemCount > 0
                ? `Slet kategorien "${confirmDelete.name}"? Dette fjerner også ${confirmDelete.itemCount} ${confirmDelete.itemCount === 1 ? 'postering' : 'posteringer'}.`
                : `Slet kategorien "${confirmDelete.name}"?`
          }
          danger
          onConfirm={performDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
